import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link, useNavigate, useSearchParams } from "react-router";
import { Bell, FileText, Plus } from "lucide-react";

import PageMeta from "../../../../shared/ui/common/PageMeta";
import Button from "../../../../shared/ui/button/Button";
import PatientFinder, {
    type PatientOption,
} from "../../../../shared/ui/patient/PatientFinder";
import { useBreakpoint } from "../../../../hooks/useBreakpoint";
import { useTherapist } from "../../../../context/useTherapist";
import { usePatientsForPayment } from "../../application/usePatientsForPayment";
import { useCourseLabels } from "../../../center";
import {
    usePayments,
    useSendPaymentReminder,
} from "../../application/usePayments";
import {
    PAYMENT_STATUSES,
    Payment,
    PaymentStatus,
    canSendReminder,
    formatMoney,
    reminderFailureFrom,
    type ReminderFailure,
} from "../../domain/types";
import PaymentStatusBadge from "../components/PaymentStatusBadge";

const PAGE_SIZE = 20;

const PaymentListPage: React.FC = () => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const { isMobile, isTablet } = useBreakpoint();
    const { therapist } = useTherapist();
    const [offset, setOffset] = useState(0);

    // The patient filter lives in the URL, not in state. It arrives that way
    // from a patient's dock, and keeping it there means the filtered list is
    // shareable and survives a refresh — which a therapist chasing one
    // person's debts will do.
    const [searchParams, setSearchParams] = useSearchParams();
    const patientId = searchParams.get("patientId") ?? undefined;
    // The status filter is in the URL for the same reasons, and so the home
    // page can link straight to what is still owed.
    const rawStatus = searchParams.get("status");
    const status = PAYMENT_STATUSES.includes(rawStatus as PaymentStatus)
        ? (rawStatus as PaymentStatus)
        : undefined;

    const { data: patientsPage } = usePatientsForPayment(therapist?.id);
    // A training centre's student, with the course they are on under the name.
    const { courseOf } = useCourseLabels();

    // The fallback lives inside the memo: `?? []` builds a new array on every
    // render, which would defeat the memo it was a dependency of.
    const patientOptions: PatientOption[] = useMemo(
        () =>
            (patientsPage?.patients ?? []).map((patient) => ({
                id: patient.id,
                name: `${patient.first_name} ${patient.last_name}`.trim(),
                detail: courseOf(patient.course_id),
            })),
        [patientsPage, courseOf]
    );

    const setParam = (key: string, next: string | null) => {
        const params = new URLSearchParams(searchParams);
        if (next) params.set(key, next);
        else params.delete(key);
        setSearchParams(params, { replace: true });
        // A filter change makes the current page meaningless — page 3 of the
        // old result set is not page 3 of the new one.
        setOffset(0);
    };

    const selectPatient = (next: string | null) => setParam("patientId", next);

    const filters = useMemo(
        () => ({ status, patientId, offset, limit: PAGE_SIZE }),
        [status, patientId, offset]
    );
    const { data, isLoading, isError, error } = usePayments(filters);

    // A therapist who bookmarked this page and later dropped the tool gets a
    // 403. "Could not load payments" would send them looking for an outage;
    // the honest answer is that the tool is off and where to turn it back on.
    const notAdopted =
        (error as { response?: { status?: number } })?.response?.status === 403;

    const items = data?.items ?? [];
    const total = data?.total ?? 0;
    const isSmallScreen = isMobile || isTablet;

    const selectStatus = (next?: PaymentStatus) => setParam("status", next ?? null);

    return (
        <>
            <PageMeta title={t("payments.title")} description={t("payments.title")} />

            <div className="mb-6 flex flex-wrap items-center justify-between gap-3">
                <h1 className="text-2xl font-semibold text-content-primary">
                    {t("payments.title")}
                </h1>
                <div className="flex items-center gap-2">
                    <Button
                        startIcon={<Plus size={16} />}
                        onClick={() => navigate("/payments/new")}
                    >
                        {t("payments.newPayment")}
                    </Button>
                </div>
            </div>

            {/* Only worth offering once there is a caseload to sift. */}
            {patientOptions.length > 1 && (
                <div className="mb-5 max-w-md">
                    <PatientFinder
                        patients={patientOptions}
                        selectedId={patientId ?? null}
                        onSelect={selectPatient}
                        label={t("payments.filters.patient")}
                    />
                </div>
            )}

            <div className="mb-5 flex flex-wrap gap-2" role="tablist">
                <FilterTab
                    label={t("payments.filters.all")}
                    active={status === undefined}
                    onClick={() => selectStatus(undefined)}
                />
                {PAYMENT_STATUSES.map((value) => (
                    <FilterTab
                        key={value}
                        label={t(`payments.status.${value}`)}
                        active={status === value}
                        onClick={() => selectStatus(value)}
                    />
                ))}
            </div>

            {isLoading && (
                <p className="text-sm text-content-tertiary">
                    {t("common.loading")}
                </p>
            )}

            {isError && notAdopted && (
                <div className="rounded-xl border border-dashed border-gray-300 p-10 text-center dark:border-gray-700">
                    <p className="mb-3 text-sm text-content-tertiary">
                        {t("payments.errors.notAdopted")}
                    </p>
                    <Link
                        to="/tools-catalog"
                        className="text-sm font-medium text-brand-600 hover:underline"
                    >
                        {t("payments.errors.notAdoptedAction")}
                    </Link>
                </div>
            )}

            {/* Load-bearing copy, not a nicety: a therapist who reads this as
                an invoicing tool will stop issuing invoices in the software
                that legally has to issue them. */}
            <p className="mb-6 rounded-lg bg-accent-tint p-3 text-sm text-accent-on-tint text-accent-text">
                {t("payments.notAnInvoiceNotice")}
            </p>

            {isError && !notAdopted && (
                <p role="alert" className="text-sm text-red-600">
                    {t("payments.errors.loadFailed")}
                </p>
            )}

            {!isLoading && !isError && items.length === 0 && (
                <div className="rounded-xl border border-dashed border-gray-300 p-10 text-center dark:border-gray-700">
                    <FileText className="mx-auto mb-3 text-content-tertiary" size={28} />
                    <p className="text-sm text-content-tertiary">
                        {t("payments.empty")}
                    </p>
                </div>
            )}

            {items.length > 0 &&
                (isSmallScreen ? (
                    <ul className="space-y-3">
                        {items.map((payment) => (
                            <PaymentCard key={payment.id} payment={payment} />
                        ))}
                    </ul>
                ) : (
                    <PaymentTable items={items} />
                ))}

            {total > PAGE_SIZE && (
                <div className="mt-6 flex items-center justify-between">
                    <Button
                        variant="outline"
                        size="sm"
                        disabled={offset === 0}
                        onClick={() => setOffset(Math.max(0, offset - PAGE_SIZE))}
                    >
                        {t("common.previous")}
                    </Button>
                    <span className="text-sm text-content-tertiary">
                        {t("payments.pagination", {
                            from: offset + 1,
                            to: Math.min(offset + PAGE_SIZE, total),
                            total,
                        })}
                    </span>
                    <Button
                        variant="outline"
                        size="sm"
                        disabled={offset + PAGE_SIZE >= total}
                        onClick={() => setOffset(offset + PAGE_SIZE)}
                    >
                        {t("common.next")}
                    </Button>
                </div>
            )}
        </>
    );
};

const FilterTab: React.FC<{
    label: string;
    active: boolean;
    onClick: () => void;
}> = ({ label, active, onClick }) => (
    <button
        type="button"
        role="tab"
        aria-selected={active}
        onClick={onClick}
        // 44px minimum touch target, per the shared layout rules.
        className={`min-h-11 rounded-lg px-4 text-sm font-medium transition ${
            active
                ? "bg-brand-500 text-white"
                : "bg-gray-100 text-gray-600 hover:bg-gray-200 dark:bg-gray-800 dark:text-gray-300"
        }`}
    >
        {label}
    </button>
);

const PaymentCard: React.FC<{ payment: Payment }> = ({ payment }) => (
    <li>
        <Link
            to={`/payments/${payment.id}`}
            className="block rounded-xl border border-gray-200 p-4 hover:bg-gray-50 dark:border-gray-700 dark:hover:bg-white/[0.03]"
        >
            <div className="mb-2 flex items-center justify-between gap-3">
                <span className="font-medium text-content-primary">
                    {payment.line_items[0]?.description ?? "—"}
                </span>
                <PaymentStatusBadge status={payment.status} />
            </div>
            <div className="flex items-center justify-between text-sm text-content-tertiary">
                <span>{payment.patient_name ?? "—"}</span>
                <span className="font-medium text-content-primary">
                    {formatMoney(payment.total)}
                </span>
            </div>
        </Link>
        <div className="mt-2 flex justify-end">
            <RemindButton payment={payment} />
        </div>
    </li>
);

/**
 * Chase this one from the list, without opening it.
 *
 * The server decides whether another reminder may go — it knows the cap and
 * the once-a-day rule — so this reads `can_send_reminder` rather than
 * re-deriving it and risking a button that disagrees with the endpoint.
 */
const RemindButton: React.FC<{ payment: Payment }> = ({ payment }) => {
    const { t } = useTranslation();
    const sendReminder = useSendPaymentReminder();
    const [failure, setFailure] = useState<ReminderFailure | null>(null);

    if (!canSendReminder(payment)) return null;

    return (
        <button
            type="button"
            disabled={sendReminder.isPending}
            onClick={(event) => {
                // The row is a link; chasing someone should not also navigate.
                event.preventDefault();
                event.stopPropagation();
                setFailure(null);
                sendReminder.mutate(payment.id, {
                    onError: (error) => setFailure(reminderFailureFrom(error)),
                });
            }}
            aria-label={t("payments.actions.sendReminder")}
            title={t("payments.actions.sendReminder")}
            className={`inline-flex items-center gap-1.5 rounded-lg border px-2.5 py-1.5 text-xs font-medium disabled:opacity-50 ${
                failure
                    ? "border-red-300 text-red-600"
                    : "border-gray-200 text-gray-500 hover:bg-gray-50 dark:border-gray-700 dark:text-gray-400 dark:hover:bg-white/[0.03]"
            }`}
        >
            <Bell size={14} />
            {failure
                ? t(`payments.reminderErrors.${failure}`)
                : t("payments.actions.sendReminder")}
        </button>
    );
};

const PaymentTable: React.FC<{ items: Payment[] }> = ({ items }) => {
    const { t } = useTranslation();
    return (
        <div className="overflow-x-auto rounded-xl border border-gray-200 dark:border-gray-700">
            <table className="w-full text-left text-sm">
                <thead className="border-b border-gray-200 text-xs uppercase text-gray-500 dark:border-gray-700 dark:text-gray-400">
                    <tr>
                        <th className="px-4 py-3">{t("payments.fields.recipient")}</th>
                        <th className="px-4 py-3">{t("payments.fields.concept")}</th>
                        <th className="px-4 py-3">
                            {t("payments.fields.externalInvoiceRef")}
                        </th>
                        <th className="px-4 py-3">{t("payments.fields.sentAt")}</th>
                        <th className="px-4 py-3">{t("payments.fields.status")}</th>
                        <th className="px-4 py-3 text-right">
                            {t("payments.fields.total")}
                        </th>
                        <th className="px-4 py-3" />
                    </tr>
                </thead>
                <tbody>
                    {items.map((payment) => (
                        <tr
                            key={payment.id}
                            className="border-b border-gray-100 last:border-0 hover:bg-gray-50 dark:border-gray-800 dark:hover:bg-white/[0.03]"
                        >
                            <td className="px-4 py-3">
                                <Link
                                    to={`/payments/${payment.id}`}
                                    className="font-medium text-brand-600 hover:underline"
                                >
                                    {payment.patient_name ?? "—"}
                                </Link>
                            </td>
                            <td className="px-4 py-3 text-content-secondary">
                                {payment.line_items[0]?.description ?? "—"}
                            </td>
                            <td className="px-4 py-3 text-content-tertiary">
                                {payment.external_invoice_ref ?? "—"}
                            </td>
                            <td className="px-4 py-3 text-content-tertiary">
                                {payment.sent_at ?? "—"}
                            </td>
                            <td className="px-4 py-3">
                                <PaymentStatusBadge status={payment.status} />
                            </td>
                            <td className="px-4 py-3 text-right font-medium text-content-primary">
                                {formatMoney(payment.total)}
                            </td>
                            <td className="px-4 py-3 text-right">
                                <RemindButton payment={payment} />
                            </td>
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
    );
};

export default PaymentListPage;
